(() => {
  const roots = document.querySelectorAll('[data-landu-minimum-purchase]');

  roots.forEach((root) => {
    if (root.dataset.landuMinimumPurchaseInitialized === 'true') return;
    root.dataset.landuMinimumPurchaseInitialized = 'true';

    const form = root.querySelector('[data-bundle-form]');
    const cards = [...root.querySelectorAll('[data-variant-card]')];
    const submit = root.querySelector('[data-add-bundle]');
    const message = root.querySelector('[data-bundle-message]');
    const selectedQuantity = root.querySelector('[data-selected-quantity]');
    const progressLabel = root.querySelector('[data-progress-label]');
    const progressBar = root.querySelector('[data-progress-bar]');
    const progressFill = root.querySelector('[data-progress-fill]');
    const totalPrices = [...root.querySelectorAll('[data-total-price]')];
    const subtotalPrices = [...root.querySelectorAll('[data-subtotal-price]')];
    const savingsPrices = [...root.querySelectorAll('[data-estimated-savings-price]')];
    const estimatedTotalPrices = [...root.querySelectorAll('[data-estimated-total-price]')];
    const savingRows = [...root.querySelectorAll('[data-estimated-saving-row]')];
    const discountStatus = root.querySelector('[data-discount-status]');
    const discountTierElements = [...root.querySelectorAll('[data-discount-tier]')];
    const minimumQuantity = Math.max(1, Number(root.dataset.minimumQuantity) || 1);
    const maxPerVariant = Number(root.dataset.maxPerVariant) || 20;
    const nativeFormGuard = root.dataset.guardNativeForm === 'true' && root.dataset.guardCurrentProduct === 'true';
    const useNativeAddToCart = root.dataset.useNativeAddToCart === 'true' && nativeFormGuard;
    const currency = root.dataset.currency || 'USD';
    const tierDiscountsEnabled = root.dataset.tierDiscountsEnabled === 'true';
    const estimatedPricingEnabled = root.dataset.estimatedPricingEnabled === 'true';
    let feedbackTimer;

    if (!form) return;

    const discountTiers = discountTierElements
      .map((element) => ({
        element,
        quantity: Number(element.dataset.discountQuantity) || 0,
        percent: Math.max(0, Math.min(100, Number(element.dataset.discountPercent) || 0)),
        label: (element.dataset.discountLabel || '').trim()
      }))
      .filter((tier) => tier.quantity > 0 && tier.label)
      .sort((a, b) => a.quantity - b.quantity);

    const setMessage = (text, type = '') => {
      if (!message) return;
      window.clearTimeout(feedbackTimer);
      message.textContent = text;
      message.classList.remove('is-error', 'is-success', 'is-active', 'is-animating');
      root.classList.remove('has-cart-success', 'has-cart-error');

      if (!text) return;

      message.classList.add('is-active');
      if (type === 'error') {
        message.classList.add('is-error');
        root.classList.add('has-cart-error');
      }
      if (type === 'success') {
        message.classList.add('is-success');
        root.classList.add('has-cart-success');
      }

      if (type === 'error' || type === 'success') {
        message.getBoundingClientRect();
        message.classList.add('is-animating');
        feedbackTimer = window.setTimeout(() => {
          message.classList.remove('is-animating');
          root.classList.remove('has-cart-success', 'has-cart-error');
        }, 1100);
      }
    };

    const getInput = (card) => card.querySelector('[data-variant-quantity]');

    const getItems = () => cards.map((card) => {
      const input = getInput(card);
      const quantity = Math.max(0, Math.min(maxPerVariant, Number.parseInt(input?.value, 10) || 0));
      return {
        card,
        input,
        quantity,
        id: card.dataset.variantId,
        unitPrice: Number(card.dataset.unitPrice) || 0,
        available: card.dataset.variantAvailable === 'true'
      };
    });

    const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2
    }).format(cents / 100);

    const getQuantityLabel = (quantity) => {
      const pluralLabel = root.dataset.itemsLabel || 'items';
      const singularLabel = root.dataset.itemLabel || (pluralLabel === 'items' ? 'item' : pluralLabel);
      return quantity === 1 ? singularLabel : pluralLabel;
    };

    const getRootUrl = () => {
      const rootUrl = window.Shopify?.routes?.root || '/';
      return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    };

    const getActiveDiscount = (total) => {
      if (!tierDiscountsEnabled || !discountTiers.length) return null;
      return discountTiers.reduce((active, tier) => {
        if (total >= tier.quantity) return tier;
        return active;
      }, null);
    };

    const updateDiscountTiers = (total) => {
      if (!tierDiscountsEnabled || !discountTiers.length) return null;

      const activeDiscount = getActiveDiscount(total);
      const nextDiscount = discountTiers.find((tier) => total < tier.quantity);

      discountTiers.forEach((tier) => {
        const isUnlocked = total >= tier.quantity;
        const isActive = activeDiscount === tier;
        tier.element.classList.toggle('is-unlocked', isUnlocked);
        tier.element.classList.toggle('is-active', isActive);
      });

      if (discountStatus) {
        if (activeDiscount) {
          discountStatus.textContent = `${root.dataset.discountUnlockedLabel || 'Unlocked'}: ${activeDiscount.label}`;
        } else if (nextDiscount) {
          const remaining = nextDiscount.quantity - total;
          discountStatus.textContent = `${root.dataset.discountNextPrefix || 'Add'} ${remaining} ${root.dataset.itemsLabel || 'items'} ${root.dataset.discountNextSuffix || 'more to unlock'} ${nextDiscount.label}`;
        } else {
          discountStatus.textContent = '';
        }
      }

      return activeDiscount;
    };

    const getEstimatedPricing = (totalPrice, activeDiscount) => {
      const percent = estimatedPricingEnabled && activeDiscount ? activeDiscount.percent : 0;
      const savings = Math.round((totalPrice * percent) / 100);
      return {
        subtotal: totalPrice,
        savings,
        total: Math.max(0, totalPrice - savings)
      };
    };

    const update = () => {
      const items = getItems();
      const total = items.reduce((sum, item) => sum + item.quantity, 0);
      const totalPrice = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
      const percentage = Math.min(100, (total / minimumQuantity) * 100);
      const activeDiscount = updateDiscountTiers(total);
      const estimatedPricing = getEstimatedPricing(totalPrice, activeDiscount);

      items.forEach((item) => {
        item.card.classList.toggle('is-selected', item.quantity > 0);
        const decreases = [...item.card.querySelectorAll('[data-quantity-decrease]')];
        const increases = [...item.card.querySelectorAll('[data-quantity-increase]')];
        decreases.forEach((decrease) => { decrease.disabled = item.quantity <= 0 || !item.available; });
        increases.forEach((increase) => { increase.disabled = item.quantity >= maxPerVariant || !item.available; });
      });

      if (selectedQuantity) selectedQuantity.textContent = total;
      if (progressLabel) progressLabel.setAttribute('aria-label', `${total} selected, ${minimumQuantity} ${getQuantityLabel(minimumQuantity)} minimum`);
      if (progressBar) progressBar.setAttribute('aria-valuenow', Math.min(total, minimumQuantity));
      if (progressFill) progressFill.style.width = `${percentage}%`;
      totalPrices.forEach((price) => { price.textContent = formatMoney(totalPrice); });
      subtotalPrices.forEach((price) => { price.textContent = formatMoney(estimatedPricing.subtotal); });
      savingsPrices.forEach((price) => { price.textContent = formatMoney(-estimatedPricing.savings); });
      estimatedTotalPrices.forEach((price) => { price.textContent = formatMoney(estimatedPricing.total); });
      savingRows.forEach((row) => { row.hidden = estimatedPricing.savings <= 0; });
      root.classList.toggle('has-estimated-saving', estimatedPricing.savings > 0);
      if (submit) submit.disabled = total < minimumQuantity;
      return { items, total, activeDiscount, estimatedPricing };
    };

    const isNativeProductForm = (nativeForm) => (
      nativeForm instanceof HTMLFormElement &&
      nativeForm !== form &&
      nativeForm.action.includes('/cart/add')
    );

    const isNativeAddToCartSubmit = (event) => {
      const submitter = event.submitter;
      if (!submitter) return true;
      if (!(submitter instanceof HTMLElement)) return false;
      return submitter.matches('[name="add"], .button-add-to-cart');
    };

    const setTriggerBusy = (trigger, busy) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.toggleAttribute('aria-busy', busy);
      if ('disabled' in trigger) trigger.disabled = busy;
    };

    const getCartItems = (items) => items
      .filter((item) => item.quantity > 0 && item.available)
      .map((item) => ({ id: Number(item.id), quantity: item.quantity }));

    const submitBundle = async (trigger) => {
      const { items, total, activeDiscount } = update();
      if (total < minimumQuantity) {
        setMessage(`Please select at least ${minimumQuantity} ${getQuantityLabel(minimumQuantity)}.`, 'error');
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const cartItems = getCartItems(items);
      const shouldRedirect = root.dataset.afterAdd === 'cart' || root.dataset.afterAdd === 'checkout';
      if (submit) submit.disabled = true;
      if (submit) submit.setAttribute('aria-busy', 'true');
      setTriggerBusy(trigger, true);
      setMessage('Adding selected items…');

      try {
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });

        if (!response.ok) throw new Error('cart_add_failed');
        const discountText = activeDiscount ? ' Automatic discount will apply if eligible.' : '';
        setMessage(`Selected items added to cart.${discountText}`, 'success');

        if (root.dataset.afterAdd === 'cart') {
          window.location.assign(root.dataset.cartUrl);
        } else if (root.dataset.afterAdd === 'checkout') {
          window.location.assign(`${getRootUrl()}checkout`);
        }
      } catch (error) {
        setMessage('We could not add the selected items. Please try again.', 'error');
        if (submit) submit.disabled = false;
        setTriggerBusy(trigger, false);
      } finally {
        if (submit) submit.removeAttribute('aria-busy');
        if (!shouldRedirect) setTriggerBusy(trigger, false);
        update();
      }
    };

    cards.forEach((card) => {
      const input = getInput(card);
      const decreases = [...card.querySelectorAll('[data-quantity-decrease]')];
      const increases = [...card.querySelectorAll('[data-quantity-increase]')];
      if (!input) return;

      decreases.forEach((decrease) => decrease.addEventListener('click', () => {
        input.value = Math.max(0, (Number.parseInt(input.value, 10) || 0) - 1);
        update();
      }));

      increases.forEach((increase) => increase.addEventListener('click', () => {
        input.value = Math.min(maxPerVariant, (Number.parseInt(input.value, 10) || 0) + 1);
        update();
      }));

      input.addEventListener('input', update);
      input.addEventListener('change', () => {
        input.value = Math.max(0, Math.min(maxPerVariant, Number.parseInt(input.value, 10) || 0));
        update();
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      await submitBundle(submit);
    });

    if (nativeFormGuard) {
      document.addEventListener('submit', async (event) => {
        const nativeForm = event.target;
        if (!isNativeProductForm(nativeForm)) return;
        if (useNativeAddToCart && !isNativeAddToCartSubmit(event)) return;
        event.preventDefault();
        event.stopPropagation();
        if (useNativeAddToCart) {
          await submitBundle(event.submitter);
        } else {
          setMessage(`Please use this selector to choose at least ${minimumQuantity} ${getQuantityLabel(minimumQuantity)}.`, 'error');
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }

    update();
  });
})();
